import { httpClient } from "./httpClient";
import {
  RequestDetailsForApprovalResponse,
  RequestLog,
  RequestResponse,
  UpdateApprovers,
} from "./types/request";

interface GenericResponse {
  success: boolean;
  errors: { index: number; errorCode: string; errorMessage: string }[];
}

export async function getRequestForApproval(
  page: number,
  perPage: number,
  searchQuery: string,
  sortItem: string,
  sortBy: string
) {
  try {
    const { data } = await httpClient.get(
      `/api/v1/request/for-approval/${page}/${perPage}/${sortItem}/${sortBy}/search?query=${searchQuery}`
    );
    return data;
  } catch (err) {
    console.error("Error: ", err);
    throw err;
  }
}

export async function getRequestForApprovalWithStatus(
  page: number,
  perPage: number,
  searchQuery: string,
  sortItem: string,
  sortBy: string,
  status: string
) {
  try {
    const { data } = await httpClient.get(
      `/api/v1/request/for-approval-status/${page}/${perPage}/${sortItem}/${sortBy}/${status}/search?query=${searchQuery}`
    );
    return data;
  } catch (err) {
    console.error("Error: ", err);
    throw err;
  }
}

export const getRequestForUAM = async (): Promise<RequestResponse[]> => {
  try {
    const { data } = await httpClient.get<{
      requests: RequestResponse[];
    }>(`/api/v1/request/uam`);
    return data.requests;
  } catch (err) {
    console.error("Error: ", err);
    throw err;
  }
};

export async function getRequestForUAMByStatus(status: string) {
  try {
    const { data } = await httpClient.post<{
      requests: RequestResponse[];
    }>(`/api/v1/request/uambystatus`, {
      statuses: status,
    });
    return data.requests;
  } catch (error) {
    throw error;
  }
}

export async function getRequestForUAMByStatusPaginated(
  page: number,
  perPage: number,
  searchQuery: string,
  sortItem: string,
  sortBy: string,
  isApproved: boolean,
  isInProgress: boolean,
  isCompleted: boolean,
  isRejected: boolean,
  isDeclined: boolean,
  isUserDeclinedAssesstation: boolean,
  searchBy: string
) {
  try {
    const { data } = await httpClient.get(
      `/api/v1/request/uambystatuspager/${page}/${perPage}/${sortItem}/${sortBy}/${isApproved}/${isInProgress}/${isCompleted}/${isRejected}/${isDeclined}/${isUserDeclinedAssesstation}/${searchBy}/search?query=${searchQuery}`
    );
    return data;
  } catch (error) {
    throw error;
  }
}

export const getRequestForMe = async (): Promise<RequestResponse[]> => {
  try {
    const { data } = await httpClient.get<{
      requests: RequestResponse[];
    }>(`/api/v1/request/for-me`);
    return data.requests;
  } catch (err) {
    console.error("Error: ", err);
    throw err;
  }
};

export const getRequestForMyTeam = async (): Promise<RequestResponse[]> => {
  try {
    const { data } = await httpClient.get<{
      requests: RequestResponse[];
    }>(`/api/v1/request/for-my-team`);
    return data.requests;
  } catch (err) {
    console.error("Error: ", err);
    throw err;
  }
};

export const getRequestMyRequests = async (): Promise<RequestResponse[]> => {
  try {
    const { data } = await httpClient.get<{
      requests: RequestResponse[];
    }>(`/api/v1/request/my-requests`);
    return data.requests;
  } catch (err) {
    console.error("Error: ", err);
    throw err;
  }
};

export const approveRequest = async (
  ids: number[],
  comments: string
): Promise<boolean> => {
  try {
    const { data } = await httpClient.post<GenericResponse>(
      `/api/v1/request/approve`,
      { requestIds: ids, comments }
    );
    return data.success;
  } catch (err) {
    console.error("Error: ", err);
    return false;
  }
};

export const updateApproversRequest = async (
  id: number,
  approvers: UpdateApprovers
): Promise<boolean> => {
  try {
    const { data } = await httpClient.post<GenericResponse>(
      `/api/v1/request/updateaprovers`,
      { id: id, approvers }
    );
    return data.success;
  } catch (err) {
    console.error("Error: ", err);
    return false;
  }
};

export const removeApproverRequest = async (
  id: number,
  level: string
): Promise<boolean> => {
  try {
    const { data } = await httpClient.post<GenericResponse>(
      `/api/v1/request/removeapprover`,
      { id: id, level }
    );
    return data.success;
  } catch (err) {
    console.error("Error: ", err);
    return false;
  }
};

const sendRequest = async (
  id: number,
  comments: string,
  operation: string
): Promise<boolean> => {
  try {
    const { data } = await httpClient.post<GenericResponse>(
      `/api/v1/request/${id}/${operation}`,
      {
        comments,
      }
    );
    return data.success;
  } catch (err) {
    console.error("Error: ", err);
    return false;
  }
};

export const resubmitMyRequest = async (payload: any): Promise<boolean> => {
  try {
    const { data } = await httpClient.put<boolean>(
      `/api/v1/request/resubmitmyrequest/${payload.id}`,
      payload
    );
    return data;
  } catch (err) {
    console.error("Error: ", err);
    throw err;
  }
};

export const sendBackRequest = async (
  id: number,
  comments: string
): Promise<boolean> => {
  return sendRequest(id, comments, "send-back");
};

export const rejectRequest = async (
  id: any,
  comments: string
): Promise<any> => {
  return sendRequest(id, comments, "reject");
};

export const bulkRejectRequest = async (
  ids: number[],
  comments: string
): Promise<boolean> => {
  try {
    const { data } = await httpClient.post<GenericResponse>(
      `/api/v1/request/bulkreject`,
      { requestIds: ids, comments }
    );
    return data.success;
  } catch (err) {
    console.error("Error: ", err);
    return false;
  }
};

export const cancelRequest = async (
  id: any,
  comments: string
): Promise<any> => {
  return sendRequest(id, comments, "cancel");
};

const mapRequestResponse = (data: RequestDetailsForApprovalResponse): any => {
  const request = data.request;

  return {
    viewRole: data.viewRole,
    request: Object.assign({}, request, {
      effectiveDate: !!request.effectiveDate ? request.effectiveDate : "",
      dateAdded: !!request.dateAdded ? request.dateAdded : "",
      advanceDate: !!request.advanceDate ? request.advanceDate : "",
    }),
  };
};

export const getRequestDetail = async (
  encryptedId: string
): Promise<RequestDetailsForApprovalResponse> => {
  try {
    const { data } = await httpClient.get<RequestDetailsForApprovalResponse>(
      `/api/v1/request/encryptId/${encryptedId}`
    );
    return mapRequestResponse(data);
  } catch (err) {
    console.error("Error: ", err);
    throw err;
  }
};

export const getRequestDetailUAM = async (
  encryptedId: string
): Promise<RequestDetailsForApprovalResponse> => {
  try {
    const { data } = await httpClient.get<RequestDetailsForApprovalResponse>(
      `/api/v1/request/uam/encryptId/${encryptedId}`
    );
    return mapRequestResponse(data);
  } catch (err) {
    console.error("Error: ", err);
    throw err;
  }
};

export const getRequestLogs = async (
  headerId: number
): Promise<RequestLog[]> => {
  try {
    const { data } = await httpClient.get<{ logEntries: RequestLog[] }>(
      `/api/v1/request/${headerId}/log`
    );
    return data.logEntries;
  } catch (err) {
    console.error("Error: ", err);
    throw err;
  }
};

export async function sendFollowUp(id: number): Promise<boolean> {
  try {
    const { data } = await httpClient.post<GenericResponse>(
      `/api/v1/request/${id}/send-follow-up`
    );

    return data.success;
  } catch (err) {
    console.error("Error: ", err);
    return false;
  }
}

export async function updateRfNumber(
  rfNum: string,
  requestId: number
): Promise<boolean> {
  try {
    const { data } = await httpClient.put(
      `/api/v1/request/uam/update-rfno/${rfNum}/${requestId}`
    );

    return data;
  } catch (err) {
    console.error("Error: ", err);
    return false;
  }
}
